import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Tag, Share2, Twitter, Linkedin, Facebook } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEOHead from '../components/SEOHead';
import MarkdownRenderer from '../components/ui/MarkdownRenderer';
import ReadingProgress from '../components/ui/reading-progress';
import TableOfContents from '../components/ui/table-of-contents';
import { BlogPost } from '../blog/types';
import { getPostBySlug, getRelatedPosts } from '../blog/registry';
import { HoverLift, GradientShift, PulseGlow } from '../components/ui/motion-primitives';

interface BlogPostPageProps {
  blogSlug?: string;
}

function getSiteBaseUrl(): string {
  return import.meta.env.PROD
    ? 'https://memara.io'
    : (import.meta.env.VITE_APP_URL || 'http://localhost:3000').replace(/\/$/, '');
}

/** Schema.org expects date or date-time; normalize YYYY-MM-DD to noon UTC. */
function toIsoDateTime(dateYmd: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateYmd)) {
    return `${dateYmd}T12:00:00.000Z`;
  }
  return dateYmd;
}

const BlogPostPage: React.FC<BlogPostPageProps> = ({ blogSlug }) => {
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showShareMenu, setShowShareMenu] = useState(false);

  useEffect(() => {
    const loadPost = async () => {
      if (!blogSlug) {
        setError('No blog post specified');
        setLoading(false);
        return;
      }

      try {
        const [blogPost, related] = await Promise.all([
          getPostBySlug(blogSlug),
          getRelatedPosts(blogSlug, 3),
        ]);

        if (blogPost) {
          setPost(blogPost);
          setRelatedPosts(related);
        } else {
          setError('Blog post not found');
        }
      } catch (err) {
        console.error('Error loading blog post:', err);
        setError('Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [blogSlug]);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = post?.title || '';

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      // Could add a toast notification here
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{error || 'Post Not Found'}</h1>
          <p className="text-gray-600 mb-8">
            {error || "The blog post you're looking for doesn't exist."}
          </p>
          <Button asChild>
            <Link to="/blog">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const baseUrl = getSiteBaseUrl();
  const canonicalUrl = `${baseUrl}/blog/${post.slug}`;
  const metaDescription = (post.seoDescription || post.excerpt).trim();
  const keywordList = [
    ...new Set([...post.tags, ...(post.seoKeywords || [])]),
  ];
  const defaultOg = `${baseUrl}/og-image.jpg`;

  const blogPostingJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: metaDescription,
    image: [defaultOg],
    datePublished: toIsoDateTime(post.date),
    dateModified: toIsoDateTime(post.lastUpdated || post.date),
    author: {
      '@type': 'Organization',
      name: post.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Memara',
      url: baseUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${baseUrl}/logos/memara-logo.svg`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    keywords: keywordList.join(', '),
    articleSection: 'Blog',
    url: canonicalUrl,
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: baseUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: `${baseUrl}/blog`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: post.title,
        item: canonicalUrl,
      },
    ],
  };

  const structuredGraphs: object[] = [blogPostingJsonLd, breadcrumbJsonLd];
  if (post.faqs && post.faqs.length > 0) {
    structuredGraphs.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: post.faqs.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    });
  }

  return (
    <>
      <SEOHead
        title={post.title}
        description={metaDescription}
        keywords={keywordList}
        canonicalUrl={canonicalUrl}
        ogType="article"
        articleMeta={{
          publishedTime: toIsoDateTime(post.date),
          modifiedTime: post.lastUpdated
            ? toIsoDateTime(post.lastUpdated)
            : undefined,
          section: 'Blog',
          tags: post.tags,
        }}
        structuredData={structuredGraphs}
      />

      <ReadingProgress />

      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="container mx-auto px-4 py-12">
          {/* Back to Blog */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-7xl mx-auto mb-8"
          >
            <Button variant="ghost" asChild>
              <Link to="/blog" className="flex items-center text-gray-600 hover:text-gray-900">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Blog
              </Link>
            </Button>
          </motion.div>

          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Table of Contents - Desktop Sidebar */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="hidden lg:block lg:col-span-1"
              >
                <div className="sticky top-24">
                  <TableOfContents content={post.content} />

                  {/* Share Section */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                  >
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Share2 className="w-5 h-5 mr-2" />
                      Share
                    </h4>
                    <div className="space-y-3">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start"
                        onClick={() => window.open(shareLinks.twitter, '_blank')}
                      >
                        <Twitter className="w-4 h-4 mr-2" />
                        Twitter
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start"
                        onClick={() => window.open(shareLinks.linkedin, '_blank')}
                      >
                        <Linkedin className="w-4 h-4 mr-2" />
                        LinkedIn
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start"
                        onClick={() => window.open(shareLinks.facebook, '_blank')}
                      >
                        <Facebook className="w-4 h-4 mr-2" />
                        Facebook
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start"
                        onClick={copyToClipboard}
                      >
                        <Share2 className="w-4 h-4 mr-2" />
                        Copy Link
                      </Button>
                    </div>
                  </motion.div>
                </div>
              </motion.div>

              {/* Article Content */}
              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="lg:col-span-3"
              >
                {/* Header */}
                <motion.header
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 lg:p-12 mb-8"
                >
                  <div className="flex flex-wrap gap-2 mb-6">
                    {post.tags.map((tag) => (
                      <Badge
                        key={tag}
                        variant="secondary"
                        className="bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                      >
                        <Tag className="w-3 h-3 mr-1" />
                        {tag}
                      </Badge>
                    ))}
                  </div>

                  <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                    {post.title}
                  </h1>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-6 text-sm text-gray-500">
                      <span className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(post.date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                      <span className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {post.readTime}
                      </span>
                      <span>by {post.author}</span>
                    </div>

                    {/* Mobile Share Button */}
                    <div className="lg:hidden">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowShareMenu(!showShareMenu)}
                      >
                        <Share2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Mobile Share Menu */}
                  {showShareMenu && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="lg:hidden mt-4 p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(shareLinks.twitter, '_blank')}
                        >
                          <Twitter className="w-4 h-4 mr-2" />
                          Twitter
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(shareLinks.linkedin, '_blank')}
                        >
                          <Linkedin className="w-4 h-4 mr-2" />
                          LinkedIn
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(shareLinks.facebook, '_blank')}
                        >
                          <Facebook className="w-4 h-4 mr-2" />
                          Facebook
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={copyToClipboard}
                        >
                          <Share2 className="w-4 h-4 mr-2" />
                          Copy
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </motion.header>

                {/* Content */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 lg:p-12 mb-8"
                >
                  <MarkdownRenderer content={post.content} />
                </motion.div>

                {/* Related Posts */}
                {relatedPosts.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8"
                  >
                    <h3 className="text-2xl font-bold text-gray-900 mb-6">Related Posts</h3>
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                      {relatedPosts.map((relatedPost, index) => (
                        <motion.article
                          key={relatedPost.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                          className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                          <HoverLift liftAmount={4}>
                          <div className="flex flex-wrap gap-1 mb-3">
                            {relatedPost.tags.slice(0, 2).map((tag: string) => (
                              <Badge key={tag} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                          <h4 className="font-semibold text-gray-900 mb-2 hover:text-indigo-600 transition-colors">
                            <GradientShift colors={["from-purple-600", "to-indigo-600"]}>
                              <Link to={`/blog/${relatedPost.slug}`}>
                                {relatedPost.title}
                              </Link>
                            </GradientShift>
                          </h4>
                          <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                            {relatedPost.excerpt}
                          </p>
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>{new Date(relatedPost.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                            <span>{relatedPost.readTime}</span>
                          </div>
                          </HoverLift>
                        </motion.article>
                      ))}
                    </div>
                  </motion.div>
                )}
              </motion.article>
            </div>
          </div>

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="max-w-4xl mx-auto text-center p-8 lg:p-12 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl shadow-xl mt-16"
          >
            <div className="bg-white bg-opacity-10 backdrop-blur-sm p-1 rounded-xl">
              <div className="bg-white rounded-xl p-8 lg:p-12">
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  Ready to Build Persistent Memory?
                </h3>
                <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                  Start building AI agents with reliable memory in minutes, not days.
                  Join developers using Memara for production-ready memory systems.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <PulseGlow color="purple" intensity={0.4}>
                    <HoverLift liftAmount={4}>
                      <Button asChild size="lg" className="bg-indigo-600 hover:bg-indigo-700">
                        <Link to="/login">
                          Get Started Free
                          <ArrowLeft className="w-5 h-5 ml-2 rotate-180" />
                        </Link>
                      </Button>
                    </HoverLift>
                  </PulseGlow>
                  <HoverLift liftAmount={4}>
                    <Button variant="outline" size="lg" asChild className="bg-white hover:bg-gray-50 border-gray-200">
                      <Link to="/blog">
                        Read More Articles
                      </Link>
                    </Button>
                  </HoverLift>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default BlogPostPage; 